import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { 
  Mail, Users, Upload, Send, Eye, RefreshCw, 
  Trash2, Edit, Plus, Filter, FileText, Home, TrendingUp,
  CheckCircle, XCircle, Clock, BookOpen, Sparkles, Link, Type, FileUp,
  Calendar, BarChart3, MousePointerClick
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import ImageUpload from '../../components/ImageUpload';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailMarketing = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('contacts');
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_segment: {}, subscribed: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Dialog states
  const [showAddContact, setShowAddContact] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBlogSelect, setShowBlogSelect] = useState(false);
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [selectedCampaignStats, setSelectedCampaignStats] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [generating, setGenerating] = useState(false);
  
  // AI Generation states
  const [aiSource, setAiSource] = useState('url'); // url, text, pdf
  const [aiUrl, setAiUrl] = useState('');
  const [aiText, setAiText] = useState('');
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiPdfFile, setAiPdfFile] = useState(null);
  
  // Form states
  const [newContact, setNewContact] = useState({ email: '', name: '', segment: 'geral' });
  const [emailForm, setEmailForm] = useState({
    subject: '',
    content: '',
    template: 'newsletter',
    segments: ['todos'],
    test_email: '',
    main_image: '',  // URL da imagem principal
    scheduled_date: '',  // Data de agendamento
    scheduled_time: ''   // Hora de agendamento
  });
  const [importFile, setImportFile] = useState(null);
  const [filterSegment, setFilterSegment] = useState('todos');
  
  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    if (!loading && isAuthenticated) {
      loadSubscribers();
      loadCampaigns();
      loadScheduledCampaigns();
      loadBlogPosts();
    }
  }, [loading, isAuthenticated, navigate]);

  const loadScheduledCampaigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/email/campaigns/scheduled`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setScheduledCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error loading scheduled campaigns:', error);
    }
  };

  const loadCampaignStats = async (campaignId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/email/campaigns/${campaignId}/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSelectedCampaignStats(response.data);
      setShowStats(true);
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao carregar estatísticas', variant: 'destructive' });
    }
  };

  const cancelScheduledCampaign = async (campaignId) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta campanha agendada?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/email/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: 'Sucesso', description: 'Campanha cancelada' });
      loadScheduledCampaigns();
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao cancelar campanha', variant: 'destructive' });
    }
  };

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      let response;
      
      if (aiSource === 'url') {
        if (!aiUrl) {
          toast({ title: 'Erro', description: 'Insira uma URL', variant: 'destructive' });
          return;
        }
        response = await axios.post(`${API_URL}/api/admin/ai/generate/from-url`, {
          url: aiUrl,
          content_type: 'email',
          language: 'pt',
          additional_instructions: aiInstructions
        }, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
      } else if (aiSource === 'text') {
        if (!aiText) {
          toast({ title: 'Erro', description: 'Insira o texto', variant: 'destructive' });
          return;
        }
        response = await axios.post(`${API_URL}/api/admin/ai/generate/from-text`, {
          text: aiText,
          content_type: 'email',
          language: 'pt',
          additional_instructions: aiInstructions
        }, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
      } else if (aiSource === 'pdf') {
        if (!aiPdfFile) {
          toast({ title: 'Erro', description: 'Selecione um PDF', variant: 'destructive' });
          return;
        }
        const formData = new FormData();
        formData.append('file', aiPdfFile);
        formData.append('content_type', 'email');
        formData.append('language', 'pt');
        formData.append('additional_instructions', aiInstructions);
        
        response = await axios.post(`${API_URL}/api/admin/ai/generate/from-pdf`, formData, {
          headers: { 
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      if (response?.data?.success && response.data.generated) {
        const gen = response.data.generated;
        setEmailForm({
          ...emailForm,
          subject: gen.subject || '',
          content: gen.content || gen.raw_content || '',
          template: 'newsletter'
        });
        setShowAIGenerate(false);
        toast({ title: 'Sucesso', description: 'Email gerado com IA!' });
        // Reset AI form
        setAiUrl('');
        setAiText('');
        setAiInstructions('');
        setAiPdfFile(null);
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao gerar conteúdo',
        variant: 'destructive'
      });
    } finally {
      setGenerating(false);
    }
  };

  const loadBlogPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/blog`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setBlogPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error loading blog posts:', error);
    }
  };

  const selectBlogPost = (post) => {
    const blogUrl = `https://yourbrokerportugal.com/blog/${post.slug || post.id}`;
    const content = `
<p>${post.excerpt}</p>
<p style="margin-top: 20px;">
  <a href="${blogUrl}" style="color: #2E5E99; font-weight: bold;">Ler artigo completo →</a>
</p>
    `.trim();
    
    setEmailForm({
      ...emailForm,
      subject: `Novo no Blog: ${post.title}`,
      content: content,
      template: 'blog'
    });
    setShowBlogSelect(false);
    toast({ title: 'Blog selecionado', description: 'Conteúdo preenchido automaticamente' });
  };

  const loadSubscribers = async () => {
    setLoadingData(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/email/subscribers`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSubscribers(response.data.subscribers || []);
      setStats(response.data.stats || { total: 0, by_segment: {}, subscribed: 0 });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar contactos',
        variant: 'destructive'
      });
    } finally {
      setLoadingData(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/email/campaigns`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/email/subscribers`, newContact, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: 'Sucesso', description: 'Contacto adicionado' });
      setShowAddContact(false);
      setNewContact({ email: '', name: '', segment: 'geral' });
      loadSubscribers();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao adicionar contacto',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteContact = async (email) => {
    if (!window.confirm('Remover este contacto?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/email/subscribers/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: 'Sucesso', description: 'Contacto removido' });
      loadSubscribers();
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao remover', variant: 'destructive' });
    }
  };

  const handleUpdateSegment = async (email, segment) => {
    try {
      const subscriber = subscribers.find(s => s.email === email);
      await axios.put(`${API_URL}/api/admin/email/subscribers/${encodeURIComponent(email)}`, {
        ...subscriber,
        segment
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      loadSubscribers();
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao atualizar', variant: 'destructive' });
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/email/subscribers/import`, formData, {
        headers: { 
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast({ 
        title: 'Sucesso', 
        description: response.data.message 
      });
      setShowImport(false);
      setImportFile(null);
      loadSubscribers();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao importar',
        variant: 'destructive'
      });
    }
  };

  const handleSyncContacts = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/email/subscribers/sync-contacts`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: 'Sucesso', description: response.data.message });
      loadSubscribers();
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao sincronizar', variant: 'destructive' });
    }
  };

  const handlePreview = async () => {
    try {
      const previewData = {
        ...emailForm,
        images: emailForm.main_image ? [emailForm.main_image] : []
      };
      const response = await axios.post(`${API_URL}/api/admin/email/campaigns/preview`, previewData, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao gerar preview', variant: 'destructive' });
    }
  };

  const handleSendEmail = async (isTest = false, isScheduled = false) => {
    if (!emailForm.subject || !emailForm.content) {
      toast({ title: 'Erro', description: 'Preencha o assunto e conteúdo', variant: 'destructive' });
      return;
    }
    
    if (isTest && !emailForm.test_email) {
      toast({ title: 'Erro', description: 'Insira um email de teste', variant: 'destructive' });
      return;
    }

    if (isScheduled && (!emailForm.scheduled_date || !emailForm.scheduled_time)) {
      toast({ title: 'Erro', description: 'Selecione data e hora para agendar', variant: 'destructive' });
      return;
    }

    setSending(true);
    try {
      // Build scheduled_at datetime if scheduling
      let scheduled_at = null;
      if (isScheduled && emailForm.scheduled_date && emailForm.scheduled_time) {
        scheduled_at = new Date(`${emailForm.scheduled_date}T${emailForm.scheduled_time}`).toISOString();
      }

      const payload = {
        subject: emailForm.subject,
        content: emailForm.content,
        template: emailForm.template,
        segments: emailForm.segments,
        test_email: isTest ? emailForm.test_email : null,
        images: emailForm.main_image ? [emailForm.main_image] : [],
        scheduled_at: scheduled_at
      };
      
      const response = await axios.post(`${API_URL}/api/admin/email/campaigns/send`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast({ 
        title: 'Sucesso', 
        description: response.data.message 
      });
      
      if (!isTest) {
        setShowCompose(false);
        setEmailForm({ subject: '', content: '', template: 'newsletter', segments: ['todos'], test_email: '', main_image: '', scheduled_date: '', scheduled_time: '' });
        loadCampaigns();
        if (isScheduled) loadScheduledCampaigns();
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao enviar email',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const filteredSubscribers = filterSegment === 'todos' 
    ? subscribers 
    : subscribers.filter(s => s.segment === filterSegment);

  const getSegmentLabel = (segment) => {
    const labels = {
      'comprador': 'Comprador',
      'investidor': 'Investidor',
      'geral': 'Geral'
    };
    return labels[segment] || segment;
  };

  const getSegmentColor = (segment) => {
    const colors = {
      'comprador': 'bg-blue-100 text-blue-700',
      'investidor': 'bg-purple-100 text-purple-700',
      'geral': 'bg-gray-100 text-gray-700'
    };
    return colors[segment] || 'bg-gray-100 text-gray-700';
  };

  const getSourceLabel = (source) => {
    const labels = {
      'form': 'Formulário',
      'import': 'Importado',
      'manual': 'Manual'
    };
    return labels[source] || source;
  };

  if (loading) {
    return (
      <AdminLayout title="Email Marketing">
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-lg text-slate-600">Carregando...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Email Marketing">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-gray-500">Total Contactos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_segment?.investidor || 0}</p>
                  <p className="text-xs text-gray-500">Investidores</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Home className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_segment?.comprador || 0}</p>
                  <p className="text-xs text-gray-500">Compradores</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Mail className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{campaigns.length}</p>
                  <p className="text-xs text-gray-500">Campanhas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          <Button
            variant={activeTab === 'contacts' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('contacts')}
            className="rounded-b-none"
          >
            <Users className="w-4 h-4 mr-2" />
            Contactos
          </Button>
          <Button
            variant={activeTab === 'compose' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('compose')}
            className="rounded-b-none"
          >
            <Mail className="w-4 h-4 mr-2" />
            Enviar Email
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('history')}
            className="rounded-b-none"
          >
            <Clock className="w-4 h-4 mr-2" />
            Histórico
          </Button>
        </div>

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setShowAddContact(true)} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
              <Button onClick={() => setShowImport(true)} variant="outline" size="sm">
                <Upload className="w-4 h-4 mr-2" />
                Importar CSV
              </Button>
              <Button onClick={handleSyncContacts} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Sincronizar Formulário
              </Button>
              <div className="flex-1" />
              <select
                value={filterSegment}
                onChange={(e) => setFilterSegment(e.target.value)}
                className="border rounded px-3 py-1 text-sm"
              >
                <option value="todos">Todos os segmentos</option>
                <option value="comprador">Compradores</option>
                <option value="investidor">Investidores</option>
                <option value="geral">Geral</option>
              </select>
            </div>

            {/* Contacts List */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Email</th>
                        <th className="text-left p-3 text-sm font-medium">Nome</th>
                        <th className="text-left p-3 text-sm font-medium">Segmento</th>
                        <th className="text-left p-3 text-sm font-medium">Origem</th>
                        <th className="text-left p-3 text-sm font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubscribers.map((sub, index) => (
                        <tr key={sub.email} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-3 text-sm">{sub.email}</td>
                          <td className="p-3 text-sm">{sub.name || '-'}</td>
                          <td className="p-3">
                            <select
                              value={sub.segment}
                              onChange={(e) => handleUpdateSegment(sub.email, e.target.value)}
                              className={`text-xs px-2 py-1 rounded border-0 ${getSegmentColor(sub.segment)}`}
                            >
                              <option value="geral">Geral</option>
                              <option value="comprador">Comprador</option>
                              <option value="investidor">Investidor</option>
                            </select>
                          </td>
                          <td className="p-3 text-sm text-gray-500">{getSourceLabel(sub.source)}</td>
                          <td className="p-3">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteContact(sub.email)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {filteredSubscribers.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-gray-500">
                            Nenhum contacto encontrado
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Compose Tab */}
        {activeTab === 'compose' && (
          <Card>
            <CardHeader>
              <CardTitle>Compor Email</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quick Actions - Import from Blog */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h4 className="font-medium text-blue-900">Criar email rapidamente</h4>
                    <p className="text-sm text-blue-700">Use IA ou selecione um blog existente</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      onClick={() => setShowAIGenerate(true)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Gerar com IA
                    </Button>
                    <Button 
                      type="button" 
                      onClick={() => setShowBlogSelect(true)}
                      variant="outline"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Escolher Blog
                    </Button>
                  </div>
                </div>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Template</label>
                <div className="flex gap-2">
                  {[
                    { value: 'newsletter', label: 'Newsletter', icon: FileText },
                    { value: 'novo_imovel', label: 'Novo Imóvel', icon: Home },
                    { value: 'blog', label: 'Artigo Blog', icon: FileText }
                  ].map(t => (
                    <Button
                      key={t.value}
                      type="button"
                      variant={emailForm.template === t.value ? 'default' : 'outline'}
                      onClick={() => setEmailForm({...emailForm, template: t.value})}
                      size="sm"
                    >
                      <t.icon className="w-4 h-4 mr-2" />
                      {t.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Segments */}
              <div>
                <label className="block text-sm font-medium mb-2">Enviar para</label>
                <div className="flex gap-2">
                  {[
                    { value: 'todos', label: 'Todos' },
                    { value: 'comprador', label: 'Compradores' },
                    { value: 'investidor', label: 'Investidores' },
                    { value: 'geral', label: 'Geral' }
                  ].map(seg => (
                    <Button
                      key={seg.value}
                      type="button"
                      variant={emailForm.segments.includes(seg.value) ? 'default' : 'outline'}
                      onClick={() => {
                        if (seg.value === 'todos') {
                          setEmailForm({...emailForm, segments: ['todos']});
                        } else {
                          const newSegments = emailForm.segments.includes(seg.value)
                            ? emailForm.segments.filter(s => s !== seg.value)
                            : [...emailForm.segments.filter(s => s !== 'todos'), seg.value];
                          setEmailForm({...emailForm, segments: newSegments.length ? newSegments : ['todos']});
                        }
                      }}
                      size="sm"
                    >
                      {seg.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium mb-2">Assunto</label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                  placeholder="Assunto do email..."
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium mb-2">Conteúdo (HTML permitido)</label>
                <Textarea
                  value={emailForm.content}
                  onChange={(e) => setEmailForm({...emailForm, content: e.target.value})}
                  placeholder="<p>Escreva o conteúdo do seu email aqui...</p>"
                  rows={10}
                />
              </div>

              {/* Imagem Principal */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <ImageUpload
                  label="Imagem Principal (opcional)"
                  value={emailForm.main_image}
                  onChange={(url) => setEmailForm({...emailForm, main_image: url})}
                />
                <p className="text-xs text-amber-700 mt-2">
                  A imagem aparecerá em destaque no topo do email, abaixo do cabeçalho.
                </p>
              </div>

              {/* Agendamento */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <label className="text-sm font-medium text-purple-900">Agendar Envio (opcional)</label>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-purple-700 mb-1">Data</label>
                    <Input
                      type="date"
                      value={emailForm.scheduled_date}
                      onChange={(e) => setEmailForm({...emailForm, scheduled_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      className="bg-white"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-purple-700 mb-1">Hora</label>
                    <Input
                      type="time"
                      value={emailForm.scheduled_time}
                      onChange={(e) => setEmailForm({...emailForm, scheduled_time: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-purple-600 mt-2">
                  Deixe vazio para enviar imediatamente. Agendado = enviado automaticamente na data/hora selecionada.
                </p>
              </div>

              {/* Test Email */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <label className="block text-sm font-medium">Teste antes de enviar</label>
                <div className="flex gap-2">
                  <Input
                    value={emailForm.test_email}
                    onChange={(e) => setEmailForm({...emailForm, test_email: e.target.value})}
                    placeholder="seu@email.com"
                    className="flex-1"
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => handleSendEmail(true, false)}
                    disabled={sending}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Teste
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handlePreview}>
                  <Eye className="w-4 h-4 mr-2" />
                  Pré-visualizar
                </Button>
                <div className="flex-1" />
                
                {/* Botão Agendar - só aparece se data/hora preenchidos */}
                {emailForm.scheduled_date && emailForm.scheduled_time && (
                  <Button 
                    type="button"
                    onClick={() => handleSendEmail(false, true)}
                    disabled={sending}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    {sending ? 'Agendando...' : 'Agendar Envio'}
                  </Button>
                )}
                
                {/* Botão Enviar Agora */}
                <Button 
                  type="button"
                  onClick={() => handleSendEmail(false, false)}
                  disabled={sending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Enviando...' : `Enviar Agora`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            {/* Campanhas Agendadas */}
            {scheduledCampaigns.length > 0 && (
              <Card className="border-purple-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-700">
                    <Calendar className="w-5 h-5" />
                    Campanhas Agendadas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {scheduledCampaigns.map((campaign) => (
                      <div key={campaign.id} className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{campaign.subject}</h4>
                            <p className="text-sm text-purple-600">
                              Agendado para {new Date(campaign.scheduled_at).toLocaleDateString('pt-PT')} às {new Date(campaign.scheduled_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => cancelScheduledCampaign(campaign.id)}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Cancelar
                          </Button>
                        </div>
                        <div className="mt-2 flex gap-2">
                          {campaign.segments?.map(seg => (
                            <span key={seg} className={`text-xs px-2 py-1 rounded ${getSegmentColor(seg)}`}>
                              {getSegmentLabel(seg)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Histórico de Campanhas Enviadas */}
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Campanhas</CardTitle>
              </CardHeader>
              <CardContent>
                {campaigns.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Nenhuma campanha enviada ainda</p>
                ) : (
                  <div className="space-y-3">
                    {campaigns.filter(c => c.status === 'sent').map((campaign) => (
                      <div key={campaign.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{campaign.subject}</h4>
                            <p className="text-sm text-gray-500">
                              Enviado em {new Date(campaign.sent_at).toLocaleDateString('pt-PT')} às {new Date(campaign.sent_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">
                              {campaign.recipients_count} enviados
                            </span>
                            {campaign.opens_count > 0 && (
                              <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
                                <MousePointerClick className="w-3 h-3" />
                                {campaign.opens_count} abertos
                              </span>
                            )}
                            {campaign.failed_count > 0 && (
                              <span className="text-sm bg-red-100 text-red-700 px-2 py-1 rounded">
                                {campaign.failed_count} falharam
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex gap-2">
                            {campaign.segments?.map(seg => (
                              <span key={seg} className={`text-xs px-2 py-1 rounded ${getSegmentColor(seg)}`}>
                                {getSegmentLabel(seg)}
                              </span>
                            ))}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadCampaignStats(campaign.id)}
                            className="text-blue-600"
                          >
                            <BarChart3 className="w-4 h-4 mr-1" />
                            Ver Estatísticas
                          </Button>
                        </div>
                        {/* Taxa de Abertura */}
                        {campaign.recipients_count > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Taxa de abertura:</span>
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full transition-all"
                                  style={{ width: `${Math.min((campaign.opens_count || 0) / campaign.recipients_count * 100, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium">
                                {((campaign.opens_count || 0) / campaign.recipients_count * 100).toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Stats Dialog */}
      <Dialog open={showStats} onOpenChange={setShowStats}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Estatísticas da Campanha
            </DialogTitle>
          </DialogHeader>
          {selectedCampaignStats && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium">{selectedCampaignStats.campaign?.subject}</h4>
                <p className="text-sm text-gray-500">
                  Enviado em {selectedCampaignStats.campaign?.sent_at && new Date(selectedCampaignStats.campaign.sent_at).toLocaleDateString('pt-PT')}
                </p>
              </div>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{selectedCampaignStats.stats?.total_sent || 0}</p>
                    <p className="text-xs text-gray-500">Enviados</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{selectedCampaignStats.stats?.total_opened || 0}</p>
                    <p className="text-xs text-gray-500">Abertos</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{selectedCampaignStats.stats?.open_rate || 0}%</p>
                    <p className="text-xs text-gray-500">Taxa de Abertura</p>
                  </CardContent>
                </Card>
              </div>

              {/* Opens List */}
              {selectedCampaignStats.stats?.opens_by_time?.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2">Quem abriu:</h5>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {selectedCampaignStats.stats.opens_by_time.map((open, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-green-50 rounded p-2 text-sm">
                        <span>{open.email}</span>
                        <span className="text-gray-500 text-xs">
                          {open.opened_at && new Date(open.opened_at).toLocaleString('pt-PT')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStats(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Contacto</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddContact} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nome</label>
              <Input
                value={newContact.name}
                onChange={(e) => setNewContact({...newContact, name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Segmento</label>
              <select
                value={newContact.segment}
                onChange={(e) => setNewContact({...newContact, segment: e.target.value})}
                className="w-full border rounded px-3 py-2"
              >
                <option value="geral">Geral</option>
                <option value="comprador">Comprador</option>
                <option value="investidor">Investidor</option>
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddContact(false)}>
                Cancelar
              </Button>
              <Button type="submit">Adicionar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar Contactos (CSV)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleImportCSV} className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg text-sm">
              <p className="font-medium mb-2">Formato do CSV:</p>
              <code className="bg-white px-2 py-1 rounded">email,name,segment</code>
              <p className="mt-2 text-gray-600">
                O segmento pode ser: comprador, investidor ou geral
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ficheiro CSV</label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowImport(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!importFile}>
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Pré-visualização do Email</DialogTitle>
          </DialogHeader>
          <div 
            className="border rounded-lg overflow-hidden"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blog Selection Dialog */}
      <Dialog open={showBlogSelect} onOpenChange={setShowBlogSelect}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Selecionar Artigo do Blog</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {blogPosts.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Nenhum post encontrado</p>
            ) : (
              blogPosts.map((post) => (
                <div 
                  key={post.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => selectBlogPost(post)}
                >
                  <div className="flex gap-4">
                    {post.image && (
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-20 h-20 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium">{post.title}</h4>
                      <p className="text-sm text-gray-500 line-clamp-2 mt-1">{post.excerpt}</p>
                      <div className="flex gap-2 mt-2">
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">{post.category}</span>
                        {post.published && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Publicado</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlogSelect(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate Dialog */}
      <Dialog open={showAIGenerate} onOpenChange={setShowAIGenerate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Gerar Email com IA
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Source Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Fonte do conteúdo</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={aiSource === 'url' ? 'default' : 'outline'}
                  onClick={() => setAiSource('url')}
                  size="sm"
                >
                  <Link className="w-4 h-4 mr-2" />
                  URL / Link
                </Button>
                <Button
                  type="button"
                  variant={aiSource === 'text' ? 'default' : 'outline'}
                  onClick={() => setAiSource('text')}
                  size="sm"
                >
                  <Type className="w-4 h-4 mr-2" />
                  Texto
                </Button>
                <Button
                  type="button"
                  variant={aiSource === 'pdf' ? 'default' : 'outline'}
                  onClick={() => setAiSource('pdf')}
                  size="sm"
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>
            </div>

            {/* URL Input */}
            {aiSource === 'url' && (
              <div>
                <label className="block text-sm font-medium mb-2">URL do conteúdo</label>
                <Input
                  value={aiUrl}
                  onChange={(e) => setAiUrl(e.target.value)}
                  placeholder="https://exemplo.com/pagina"
                />
                <p className="text-xs text-gray-500 mt-1">Cole o link de um imóvel, notícia ou página</p>
              </div>
            )}

            {/* Text Input */}
            {aiSource === 'text' && (
              <div>
                <label className="block text-sm font-medium mb-2">Texto de referência</label>
                <Textarea
                  value={aiText}
                  onChange={(e) => setAiText(e.target.value)}
                  placeholder="Cole aqui o texto sobre o imóvel, região ou assunto..."
                  rows={6}
                />
              </div>
            )}

            {/* PDF Input */}
            {aiSource === 'pdf' && (
              <div>
                <label className="block text-sm font-medium mb-2">Ficheiro PDF</label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setAiPdfFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-gray-500 mt-1">Upload de brochura, ficha técnica ou documento</p>
              </div>
            )}

            {/* Additional Instructions */}
            <div>
              <label className="block text-sm font-medium mb-2">Instruções adicionais (opcional)</label>
              <Textarea
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                placeholder="Ex: Foque nos benefícios para investidores, mencione o Golden Visa..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIGenerate(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={generateWithAI}
              disabled={generating}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Gerar Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default EmailMarketing;
